import {Icon} from '@benchling/nucleus-ui';
import {Alert, Anchor, Box, Button, createStyles, Group, Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {warnAndRedirectAfterInaccessiblePageOpen} from 'auth/capabilities/InaccessibleFeaturesActions';
import {navigate, pluralize, redirectForDownload} from 'BenchlingUtil';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {
  EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
  ErrorData,
  RowErrorCode,
  SpreadsheetValidationError,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import {SIDE_PADDING} from 'chemistry/ChemicalCounterpartConstants';
import {ChemicalCounterpartCSVValidationOutput} from 'chemistry/ChemistryDAO';
import makeCancelable from 'lib/makeCancelable';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as ImportUtil from 'util/ImportUtil';
import {withLoadingScreen} from 'util/Promises';

interface ValidationResult {
  importUrl: string;
  csvValidationOutput: ChemicalCounterpartCSVValidationOutput;
  errorData: ErrorData;
}

function errorsForRowValidation(
  output: ChemicalCounterpartCSVValidationOutput['rowValidationOutputs']
): ErrorData {
  if (output == null) {
    return EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO;
  }
  const otherErrors: Array<SpreadsheetValidationError> = [];
  for (const [row, rowOutput] of output.entries()) {
    // errorMessages is empty for valid rows, so this naturally skips them.
    for (const errorMessage of rowOutput.errorMessages) {
      otherErrors.push({row, errorCode: RowErrorCode.OTHER_ERROR, errorMessage});
    }
  }
  return {...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO, otherErrors};
}

async function performAsyncWork(
  selectedFile: File,
  validate: (filename: string, url: string) => Promise<ChemicalCounterpartCSVValidationOutput>
): Promise<ValidationResult> {
  const url = await ImportUtil.upload(selectedFile);
  if (!url) {
    throw new Error('Upload did not return a URL');
  }
  const output = await validate(selectedFile.name, url);
  return {
    importUrl: url,
    csvValidationOutput: output,
    errorData: errorsForRowValidation(output.rowValidationOutputs),
  };
}

export interface ChemicalCounterpartCreateFromCSVPageProps {
  /** Singular lowercase name of the imported type, e.g. "salt". */
  counterpartName: string;
  libraryUrl: string;
  templateCSVLink: string;
  validate: (filename: string, url: string) => Promise<ChemicalCounterpartCSVValidationOutput>;
  createFromFile: (url: string, filename: string) => Promise<{createdIds: Array<string>}>;
  onImportSuccess?: () => Promise<void>;
  dataTestComponent: string;
}

export default function ChemicalCounterpartCreateFromCSVPage({
  counterpartName,
  libraryUrl,
  templateCSVLink,
  validate,
  createFromFile,
  onImportSuccess,
  dataTestComponent,
}: ChemicalCounterpartCreateFromCSVPageProps): JSX.Element | null {
  const {classes} = useStyles();
  const isTenantAdmin = useStoreSelector(
    EntityStore,
    useCallback((store: typeof EntityStore) => store.currentUser()?.isTenantAdmin ?? false, [])
  );
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const cancelRef = useRef<(() => void) | undefined>();

  const counterpartNamePlural = pluralize(2, counterpartName);
  const isValidationLoading = file != null && validationResult == null;
  const csvValidationOutput = validationResult?.csvValidationOutput ?? null;
  const errorData = validationResult?.errorData ?? EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO;
  const importUrl = validationResult?.importUrl ?? null;

  useEffect(() => {
    if (!isTenantAdmin) {
      warnAndRedirectAfterInaccessiblePageOpen(
        `You must be a tenant admin to import ${counterpartNamePlural}. Please contact your tenant admin for more information.`
      );
    }
  }, [isTenantAdmin, counterpartNamePlural]);

  const canSubmit =
    file != null &&
    !isValidationLoading &&
    csvValidationOutput != null &&
    csvValidationOutput.fileValidationError == null &&
    (csvValidationOutput.rowValidationOutputs?.every((output) => output.isValid) ?? false);

  const replaceSelectedFile = (newFile: File | null): void => {
    cancelRef.current?.();
    setFile(newFile);
    setValidationResult(null);
  };

  const uploadAndValidateCSV = async (selectedFile: File): Promise<void> => {
    replaceSelectedFile(selectedFile);

    const {promise, cancel} = makeCancelable(performAsyncWork(selectedFile, validate));
    cancelRef.current = cancel;
    try {
      const result = await promise;
      setValidationResult(result);
    } catch (e) {
      if ((e as {isCanceled?: boolean}).isCanceled) {
        return;
      }
      replaceSelectedFile(null);
      throw e;
    }
  };

  const clearSelectedFile = (): void => {
    replaceSelectedFile(null);
  };

  const createCounterparts = async (): Promise<void> => {
    if (importUrl == null || file == null) {
      return;
    }
    const {createdIds} = await createFromFile(importUrl, file.name);
    notifications.success(`Imported ${createdIds.length} ${pluralize(createdIds.length, counterpartName)}`);
    await onImportSuccess?.();
    navigateToURL(libraryUrl);
  };

  if (!isTenantAdmin) {
    return null;
  }

  return (
    <Stack
      h="100%"
      spacing={0}
      className={classes.page}
      {...getTestProps({component: dataTestComponent, element: 'root'})}
    >
      <Box className={classes.header}>
        <Button
          variant="ghost"
          compact={true}
          px={0}
          onClick={() => {
            navigateToURL(libraryUrl);
          }}
        >
          {`Back to ${counterpartName} library`}
        </Button>
        <Title order={1}>{`Import ${counterpartNamePlural} from CSV`}</Title>
      </Box>
      <Stack spacing={4} className={classes.body}>
        <Box className={classes.wideContent}>
          <Alert
            variant="info"
            icon={<Icon icon="info-circle" />}
            // Keep in sync with _REQUIRED_COLUMNS/_ALLOWED_COLUMNS in
            // benchling/chemistry/salts_solvents/csv_util.py
            title='The CSV file must define "name" and "smiles" columns. It can also include an optional "code" column.'
          >
            <Stack spacing="xs" align="flex-start">
              <Text>{`Each row creates one ${counterpartName}. Codes must be unique across all salts and solvents.`}</Text>
              <Anchor
                href={templateCSVLink}
                onClick={(e) => {
                  e.preventDefault();
                  redirectForDownload(templateCSVLink);
                }}
              >
                Download CSV template
              </Anchor>
            </Stack>
          </Alert>
        </Box>
        <Box className={classes.fileChooserContainer}>
          {file == null ? (
            <FileChooser onSelectFiles={uploadAndValidateCSV} multiple={false} />
          ) : (
            <FileList>
              <FileRow file={file} onRequestRemove={clearSelectedFile} />
            </FileList>
          )}
        </Box>
        <Text>Upload review</Text>
        <Text size="sm" c="dimmed" mb="md">
          Review spreadsheet interpretation and errors
        </Text>
        {file == null ? null : isValidationLoading ? (
          <CenteredHorizontalThrobber />
        ) : csvValidationOutput?.fileValidationError != null ? (
          <Box className={classes.wideContent}>
            <Alert variant="error" icon={<Icon icon="exclamation-circle" />}>
              {csvValidationOutput.fileValidationError}
            </Alert>
          </Box>
        ) : (
          <ValidationErrorsTable
            spreadsheetType={SpreadsheetType.SPREADSHEET}
            resolvableErrorStatuses={{}}
            errorData={errorData}
          />
        )}
      </Stack>
      <Group position="right" className={classes.footer}>
        <Button variant="secondary" component="a" href={libraryUrl} onClick={navigate}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => withLoadingScreen(createCounterparts(), 'Importing...')}
          {...getTestProps({component: dataTestComponent, element: 'createButton'})}
        >
          {`Import ${counterpartNamePlural}`}
        </Button>
      </Group>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  page: {
    overflowY: 'auto',
  },
  header: {
    marginInline: SIDE_PADDING,
    marginTop: theme.spacing.md,
  },
  body: {
    flex: 1,
    marginInline: SIDE_PADDING,
    marginTop: theme.spacing.md,
  },
  wideContent: {
    maxWidth: 1050,
  },
  fileChooserContainer: {
    maxWidth: 450,
    minHeight: 100,
  },
  footer: {
    padding: theme.spacing.md,
  },
}));
